import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import {
  formatPhoneDigits,
  formatPhoneInput,
  isCompletePhoneInput,
  normalizePhone,
  phoneInputDigits,
} from './phone';

/** OTHER — полный международный номер, включая код страны. */
export type InternationalPhoneCountry = CountryCode | 'OTHER';

export interface NormalizedInternationalPhone {
  /** Канонический ключ поиска и дублей: только цифры, без плюса. */
  e164: string;
  formatted: string;
  country: InternationalPhoneCountry;
}

const MAX_INTERNATIONAL_DIGITS = 15;
const PHONE_SEPARATORS = /[,;/\n|]+/;

const digitsOnly = (value: string): string => value.replace(/\D/g, '');

/**
 * Международный номер без привязки к двум историческим колонкам РБ/РФ.
 * Проверяем код страны, длину и общий шаблон национального номера по min-
 * справочнику. Одна длина не годится: короткий служебный номер и недописанный
 * мобильный могут содержать одинаковое число цифр.
 */
export function normalizeInternationalPhone(
  input: string | null | undefined,
  hint?: InternationalPhoneCountry,
): NormalizedInternationalPhone | null {
  if (!input?.trim()) return null;
  if (hint === 'BY' || hint === 'RU') return normalizePhone(input, hint);

  let value = input.trim();
  // Парсер не должен вынимать один телефон из произвольной заметки или списка.
  if (!/^\+?[\d\s().-]+$/.test(value)) return null;
  if (value.startsWith('00')) value = `+${value.slice(2)}`;
  if (digitsOnly(value).length > MAX_INTERNATIONAL_DIGITS) return null;
  if ((!hint || hint === 'OTHER') && !value.startsWith('+')) value = `+${value}`;

  const parsed = parsePhoneNumberFromString(value, {
    defaultCountry: hint && hint !== 'OTHER' ? hint : undefined,
    extract: false,
  });
  if (!parsed?.isValid()) return null;
  if (hint && hint !== 'OTHER' && parsed.country !== hint) return null;

  const e164 = parsed.number.slice(1);
  // Страну определяет справочник: +7 7xx относится к Казахстану, не к РФ.
  // Привычный вид белорусских и российских номеров при этом не меняется.
  if (parsed.country === 'BY' || parsed.country === 'RU') {
    return normalizePhone(e164, parsed.country);
  }
  return {
    e164,
    formatted: parsed.formatInternational(),
    country: parsed.country ?? 'OTHER',
  };
}

export interface InternationalPhoneListItem {
  raw: string;
  normalized: NormalizedInternationalPhone | null;
}

export function parseInternationalPhoneList(
  input: string | null | undefined,
  hint?: InternationalPhoneCountry,
): InternationalPhoneListItem[] {
  return (input ?? '')
    .split(PHONE_SEPARATORS)
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && !/^[-–—.\s]+$/.test(part))
    .map((raw) => ({ raw, normalized: normalizeInternationalPhone(raw, hint) }));
}

export function internationalPhoneListE164(
  input: string | null | undefined,
  hint?: InternationalPhoneCountry,
): string[] {
  return [...new Set(parseInternationalPhoneList(input, hint).flatMap((item) =>
    item.normalized ? [item.normalized.e164] : [],
  ))];
}

export function isValidInternationalPhoneList(
  input: string,
  hint?: InternationalPhoneCountry,
): boolean {
  const items = parseInternationalPhoneList(input, hint);
  return items.length > 0 && items.every(({ normalized }) =>
    normalized !== null && (!hint || hint === 'OTHER' || normalized.country === hint),
  );
}

/** Повторы убираем по номеру; пометки и нераспознанные значения сохраняем. */
export function dedupeInternationalPhoneList(
  input: string | null | undefined,
  hint?: InternationalPhoneCountry,
): string {
  const seen = new Set<string>();
  return parseInternationalPhoneList(input, hint)
    .filter(({ normalized }) => {
      if (!normalized) return true;
      if (seen.has(normalized.e164)) return false;
      seen.add(normalized.e164);
      return true;
    })
    .map(({ raw }) => raw)
    .join('\n');
}

export function internationalPhoneForCopy(
  input: string | null | undefined,
  hint?: InternationalPhoneCountry,
): string {
  const items = parseInternationalPhoneList(input, hint);
  if (items.length === 0) return (input ?? '').trim();
  return items.map(({ raw, normalized }) => normalized ? `+${normalized.e164}` : raw).join(', ');
}

/**
 * Для выбранной страны — национальные цифры, для OTHER — вместе с кодом.
 * Код +7 у Казахстана снимаем только как явный префикс или целый номер:
 * национальный 701… начинается с семёрки, и терять её при наборе нельзя.
 */
export function internationalPhoneInputDigits(
  input: string,
  country: InternationalPhoneCountry,
): string {
  if (country === 'BY' || country === 'RU') return phoneInputDigits(input, country);
  let value = input.trim();
  if (value.startsWith('00')) value = `+${value.slice(2)}`;
  if (country === 'OTHER') return digitsOnly(value).slice(0, MAX_INTERNATIONAL_DIGITS);

  const callingCode = getCountryCallingCode(country);
  const prefix = `+${callingCode}`;
  const maxLength = MAX_INTERNATIONAL_DIGITS - callingCode.length;
  if (value.startsWith(prefix)) {
    const national = digitsOnly(value.slice(prefix.length));
    // При последовательном наборе «7701…» первая семёрка неоднозначна.
    // Убираем её только после целого номера, если он иначе не помещается
    // в план выбранной страны; короткий национальный «701…» не трогаем.
    if (national.startsWith(callingCode)) {
      const full = parsePhoneNumberFromString(`+${national}`);
      const withPrefix = parsePhoneNumberFromString(`${prefix}${national}`);
      if (full?.country === country && full.isValid() && !withPrefix?.isValid()) {
        return full.nationalNumber;
      }
    }
    return national.slice(0, maxLength);
  }

  const formatter = new AsYouType(country);
  formatter.input(value);
  return formatter.getNationalNumber().slice(0, maxLength);
}

/** Готовые цифры больше не разбираем: это важно при стирании номера. */
export function formatInternationalPhoneDigits(
  digits: string,
  country: InternationalPhoneCountry,
): string {
  if (country === 'BY' || country === 'RU') return formatPhoneDigits(digits, country);
  if (!digits) return '';
  const callingCode = country === 'OTHER' ? '' : getCountryCallingCode(country);
  const value = `+${callingCode}${digitsOnly(digits).slice(0, MAX_INTERNATIONAL_DIGITS - callingCode.length)}`;
  return new AsYouType().input(value);
}

export function formatInternationalPhoneInput(
  input: string,
  country: InternationalPhoneCountry,
): string {
  if (country === 'BY' || country === 'RU') return formatPhoneInput(input, country);
  return formatInternationalPhoneDigits(internationalPhoneInputDigits(input, country), country);
}

export function isCompleteInternationalPhoneInput(
  input: string,
  country: InternationalPhoneCountry,
): boolean {
  if (country === 'BY' || country === 'RU') return isCompletePhoneInput(input, country);
  return normalizeInternationalPhone(input, country) !== null;
}

export interface InternationalPhoneCountryOption {
  country: CountryCode;
  label: string;
  /** Только цифры: плюс добавляет интерфейс. */
  callingCode: string;
}

/** Страны и коды берутся из того же справочника, что проверяет номера. */
export function getInternationalPhoneCountries(): InternationalPhoneCountryOption[] {
  const names = new Intl.DisplayNames(['ru'], { type: 'region' });
  return getCountries()
    .map((country) => ({
      country,
      label: names.of(country) ?? country,
      callingCode: getCountryCallingCode(country),
    }))
    .sort((left, right) => left.label.localeCompare(right.label, 'ru'));
}

export function internationalPhonePlaceholder(country: InternationalPhoneCountry): string {
  if (country === 'OTHER') return '+код страны и номер';
  if (country === 'BY') return '+375 (__) ___-__-__';
  if (country === 'RU') return '+7 (___) ___-__-__';
  const prefix = `+${getCountryCallingCode(country)}`;
  const example = getExampleNumber(country, examples)?.formatInternational();
  return example ? `${prefix}${example.slice(prefix.length).replace(/\d/g, '_')}` : `${prefix} номер телефона`;
}
